import React, { useEffect, useState, useMemo } from "react";
import { RegistrationNoticesType } from "../../static/types/commonTypes/registrationNoticesType";
import useValidation from "../../hooks/useValidation";
import { registrationNoticesValidationSchema } from "../../utils/validation/registrationNoticesValidationSchema";
import useAppContext from "../../hooks/useAppContext";
import { AppContextType } from "../../context/AppContext";

type RegistrationNoticesProps = {
  checked: boolean;
  registrationNotices: RegistrationNoticesType;
  onValidationChange: (isValid: boolean) => void;
  changeRegistrationNotices: (registrationNotices: Partial<RegistrationNoticesType>) => void;
};

const requiredFields = {
  bordereau: ["numeroBordereau", "numeroCase"],
  dossier: ["numeroDossier"]
};

export default function RegistrationNoticesComponent({
  checked,
  registrationNotices,
  onValidationChange,
  changeRegistrationNotices
}: RegistrationNoticesProps) {

  const { errors, isValid: isDataValid } = useValidation(
    registrationNotices,
    registrationNoticesValidationSchema
  ) as { errors: Record<string, any>, isValid: boolean };

  const {

    typeRegistrationData,
    setTypeRegistrationData,

  } = useAppContext() as AppContextType

  useEffect(() => {
    onValidationChange(isDataValid);
  }, [isDataValid, onValidationChange]);

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'bordereau' | 'dossier';
    setTypeRegistrationData(value);
  
    changeRegistrationNotices(
      value === 'bordereau'
        ? { numberProvided: 'bordereau', bordereau: { numeroBordereau: '', numeroCase: '', numeroExtrait: '' }, dossier: null }
        : { numberProvided: 'dossier', dossier: { numeroDossier: '', numeroReference: '' }, bordereau: null }
    );
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
  
    changeRegistrationNotices(
      typeRegistrationData === 'bordereau' && registrationNotices.bordereau
        ? { bordereau: { ...registrationNotices.bordereau, [id]: value } }
        : typeRegistrationData === 'dossier' && registrationNotices.dossier
        ? { dossier: { ...registrationNotices.dossier, [id]: value } }
        : {}
    );
  };

  const selectedData = useMemo(() => (typeRegistrationData === 'bordereau' ? registrationNotices.bordereau : registrationNotices.dossier), [typeRegistrationData, registrationNotices]);

  const isFieldRequired = (fieldName: string): boolean => {
    return requiredFields[typeRegistrationData]?.includes(fieldName) ?? false;
  };

  return (
    <div className = "container p-4">

      <div className = "row mb-2">

        <label htmlFor="registrationNotices" className = "col-4 col-form-label fw-bold mb-1">
          Numéro renseigné <strong className = "text-danger">*</strong>
        </label>

        <div className = "col-6">

          <select
            className = { `form-select ${ checked && errors?.typeRegistrationData ? "is-invalid" : ""}` }
            id = "registrationNotices"
            onChange = { handleTypeChange }
            value = { typeRegistrationData }
          >
            <option value="bordereau">N° de bordereau et N° d'extrait</option>
            <option value="dossier">N° du dossier</option>
          </select>

          {
            checked && errors?.typeRegistrationData &&
            <div className = "invalid-feedback"> { errors.typeRegistrationData } </div>
          }

        </div>

      </div>

      {
        selectedData &&
        <div className = "mt-2">
          {
            Object.entries(selectedData).map(([key, value]) => (

            <div key={key} className = "row mb-2">

              <label htmlFor={key} className = "col-4 col-form-label fw-bold mb-1">
                { 
                  key === "numeroBordereau"
                    ? "N° de bordereau"
                    : key === "numeroCase"
                    ? "N° de case"
                    : key === "numeroExtrait"
                    ? "N° d'extrait"
                    : key === "numeroDossier"
                    ? "N° du dossier"
                    : "N° de référence"
                }
                { isFieldRequired(key) && <strong className = "text-danger">*</strong> }
              </label>

              <div className = "col-6">
                <input
                  className = {`form-control ${
                    checked && errors?.[`${ typeRegistrationData }.${key}`] ? "is-invalid" : ""
                  }`}
                  type="text"
                  id={key}
                  value={value || ""}
                  onChange={handleInputChange}
                />
                {
                  checked && errors?.[`${ typeRegistrationData }.${key}`] && 
                  <div className = "invalid-feedback">{ errors[`${ typeRegistrationData }.${key}`]}</div>
                }
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
